/* eslint-disable max-lines */
// deps
import { useState, useRef, useEffect, useCallback, useContext } from "react";

// internal-deps
import {
    CheckoutContext,
    getBridgeSdk,
    DineoutCartContext,
    DineoutCartType,
    DineoutCartActionTypes,
    getDineoutFlowType,
    ISuperSavingBottomWidgetProps,
    NEWRELIC_TABLES,
    recordNREvent,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    SCENES_FULL_PAYMENT_ORDER_ID_KEY,
} from "@swiggy-private/checkout-core";
import { captureError, randomStringIdGenerator, useEffectOnce, Storage, isAppPlatform } from "@swiggy-private/core-ui";

// constants
import {
    HTTP_STATUS_CODES,
    OOPS_SOMETHING_WENT_WRONG,
    CART_META_DATA_TYPE,
} from "@dineout-cart-client/constants/index";
import { TEST_GET_DINEOUT_CART } from "@dineout-cart-client/constants";

// interfaces

import {
    ICartSelectedSlot,
    IDineoutCartResponse,
    IDineoutWindowCartApiData,
    IErrorMeta,
} from "@dineout-cart-client/interfaces";
import { ILocalCart, ILocalCartResponseV2 } from "@dineout-cart-client/interfaces/LocalCart";

// apis
import { updateDineoutCartWrapper } from "@dineout-cart-client/apis/Cart";

// contexts
import { DineoutSharedCartContext } from "@dineout-cart-client/contexts/DineCashPoolingContext";

// reducers
import { prepareLocalCartFromReviewedCart } from "@dineout-cart-client/utils/TableReservationUtils";

// utils
import {
    fetchLocalCart,
    getLatestCartResponse,
    getLocalDineoutCart,
    isEventReservationCart,
    isScenesFullPaymentCart,
    isStealStoreCart,
    createUpdatedLocalCart,
} from "@dineout-cart-client/helpers/cartHelper";
import { toFlatLocalCart } from "@dineout-cart-client/helpers/LocalCart";
import { cloneDeep, isEmpty } from "@swiggy-private/js-utils";
import { CUSTOM_EVENTS } from "@dineout-cart-client/constants/CustomEvents";
import { Result, Ok } from "@swiggy-private/js-fp";
import { ApiResponse } from "@swiggy-private/core-utils";

// hooks
import useRedirectToPayment from "./useRedirectToPayment";
import { prepareStealDealLocalCartFromReviewedCart } from "@dineout-cart-client/helpers/StealDealCart";

interface CartError {
    httpStatusCode?: number;
    errorPayload?: IErrorMeta;
}

interface ICartStateData {
    loading: boolean;
    error?: CartError;
    cartState?: IDineoutCartResponse["data"];
    selectedOfferId?: string;
    selectedSlot?: ICartSelectedSlot;
    superSavingBottomWidget?: ISuperSavingBottomWidgetProps;
}

const useFetchDineoutCart = (cartType: DineoutCartType | null) => {
    const apiCallRef = useRef(false);
    const cartApiDataConsumedRef = useRef(false);
    const [state, setState] = useState<ICartStateData>({ loading: true });

    const { redirect: redirectToPayment } = useRedirectToPayment({
        skipPaymentLink: true,
    });
    // Ref mirror so getCartDataForScenesFullPayment stays stable when redirectToPayment
    // changes on payment-page navigation (avoids re-triggering the cart-fetch effect).
    const redirectToPaymentRef = useRef(redirectToPayment);
    redirectToPaymentRef.current = redirectToPayment;

    const { setCartState, cartState, setCartType, uniqueIdForCart, setUniqueIdForCart } =
        useContext(DineoutCartContext);
    const {
        returnedFromPoolPage,
        setReturnedFromPoolPage,
        contributionsArrivedOnPoolPage,
        setContributionsArrivedOnPoolPage,
    } = useContext(DineoutSharedCartContext);

    // Ref mirror so updateCartApiResponseHandler can read the latest uniqueIdForCart
    // without adding it to the useCallback dep chain (which would recreate getCartData).
    const uniqueIdForCartRef = useRef(uniqueIdForCart);
    uniqueIdForCartRef.current = uniqueIdForCart;
    // Ref mirrors for pool-page flags so updateCartApiResponseHandler can read them
    // without adding them to the useCallback dep chain.
    const returnedFromPoolPageRef = useRef(returnedFromPoolPage);
    returnedFromPoolPageRef.current = returnedFromPoolPage;
    const contributionsArrivedRef = useRef(contributionsArrivedOnPoolPage);
    contributionsArrivedRef.current = contributionsArrivedOnPoolPage;
    const { checkoutState } = useContext(CheckoutContext);

    useEffectOnce(() => {
        /*
         * This cartType setting should ideally be at the top-most level(CartMain, router etc)
         * but there is a race condition issue due to which it is fetched first before its value is set.
         * To resolve this, it is kept here to ensure that there is no race condition as it is a critical flag.
         */
        setCartType(cartType);
    });

    const handleError = (err: { error: IErrorMeta }, localCartData?: ILocalCart) => {
        const httpStatusCode = err?.error?.status;
        const errorObj: CartError = {
            errorPayload: err?.error,
        };

        if (httpStatusCode === HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
            errorObj.httpStatusCode = httpStatusCode;
        }

        apiCallRef.current = false;
        setState({
            loading: false,
            error: errorObj,
        });

        // this we need to do cleanup in case of non-zero statuscode, or any https error
        if (isStealStoreCart(cartType) && localCartData) {
            const preparedLocalCart = {
                ...localCartData,
                flowType: getDineoutFlowType(cartType),
                latestCartCallTimestamp: 0,
                latestCartResponse: undefined,
            };

            getBridgeSdk()
                .dineoutUpdateLocalCart(preparedLocalCart)
                .catch(() => void 0);
        }
    };

    const fetchLocalCartFromNative = useCallback(() => fetchLocalCart(cartType), [cartType]);

    const updateCartApiResponseHandler = useCallback(
        (
            response: Result<IDineoutCartResponse, Error | ApiResponse>,
            updatedLocalCart: ILocalCart,
            localCart: ILocalCart,
        ) => {
            if (response.isErr()) {
                // TODO: need to update this via  type: DineoutCartActionTypes.UPDATE_ERROR_PAYLOAD
                setCartState({
                    type: DineoutCartActionTypes.UPDATE_LOCAL_CART,
                    payload: updatedLocalCart,
                });
                throw response;
            }

            const cartResponse = response.unwrap();
            setState({
                loading: false,
            });
            setCartState({
                type: DineoutCartActionTypes.UPDATE_LOCAL_AND_REVIEWED_CART,
                payload: {
                    localCart: updatedLocalCart,
                    reviewedCart: cartResponse || null,
                },
            });

            // Refresh the payment link only when the user returned from the pool page AND
            // contributions actually arrived while they were there. The flag is set by
            // useDineCashPolling on the pool page via two mechanisms:
            //   1. lastUpdatedAt changed (non-null baseline) → pool was updated.
            //   2. null baseline + first poll: totalAmountReceived > 0 → contributions
            //      exist even before the first poll fired (first-time pooling case).
            if (returnedFromPoolPageRef.current && contributionsArrivedRef.current) {
                setUniqueIdForCart(randomStringIdGenerator());
                setReturnedFromPoolPage(false);
                setContributionsArrivedOnPoolPage(false);
            }

            if (isEventReservationCart(cartType) || isStealStoreCart(cartType)) {
                //  This update local cart on pageload is done to ensure that in case of OOS items, native local cart
                //  needs to be updated to ensure parity and UX especially in case of high demand event sale

                /* Flow type is required for the dineout cart bridge functions, so that app can identify which cart flow is being used e.g. table reservation, event booking or bill payment */
                /* If we don't pass the flow type, the bridge functions will not be able to identify which cart flow to update/delete 
                /* and this might create issues when the user is using multiple carts in the same app session */
                const flowType = getDineoutFlowType(cartType);
                const preparedLocalCart = isStealStoreCart(cartType)
                    ? prepareStealDealLocalCartFromReviewedCart(
                          flowType,
                          toFlatLocalCart(updatedLocalCart),
                          cartResponse,
                      )
                    : prepareLocalCartFromReviewedCart(flowType, localCart, cartResponse.data);

                getBridgeSdk()
                    .dineoutUpdateLocalCart(preparedLocalCart)
                    .catch(() => void 0);
            }
        },
        [cartType, setCartState, setUniqueIdForCart, setReturnedFromPoolPage, setContributionsArrivedOnPoolPage],
    );

    const getCartDataForScenesFullPayment = useCallback(() => {
        const latitude = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat);
        const longitude = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng);

        // Read orderId from session storage for scenes full payment flow
        const orderId = Storage.getItem<string>(SCENES_FULL_PAYMENT_ORDER_ID_KEY) || "";

        const payload: ILocalCart = {
            cartMetaData: {
                orderId: orderId,
                couponCode: null,
                requestTimestamp: Date.now() / 1000,
                restaurantId: "",
                type: CART_META_DATA_TYPE,
            },
            location: {
                latitude: String(latitude || 0),
                longitude: String(longitude || 0),
            },
        };

        updateDineoutCartWrapper(cartType, payload)
            .then((response) => {
                if (response.isErr()) {
                    throw response;
                }

                recordNREvent({
                    eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CART_P0_COMPONENT_IMPRESSION,
                    componentName: "scenes_full_payment_cart_api_success",
                });

                setState((prevState) => ({
                    ...prevState,
                    loading: false,
                }));

                // Call redirect callback directly on success
                redirectToPaymentRef.current?.();
            })
            .catch(handleError);
    }, [cartType]);

    const getCartData = useCallback(() => {
        if (checkoutState.payload?.skipCartReload) {
            // Skip reload while redirect flag is set, but keep apiCallRef
            // clear so we can retry once the flag is unset.
            apiCallRef.current = false;
            setState({ loading: false });
            return;
        }

        const __IS_TEST__ = localStorage.getItem(TEST_GET_DINEOUT_CART);

        const fetchLocalCartFn = __IS_TEST__ ? getLocalDineoutCart : fetchLocalCartFromNative;

        fetchLocalCartFn()
            .then((localCart) => {
                if (!localCart) {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CART_LOCAL_CART_ERROR,
                        error: "empty_cart_from_native_getCartData",
                        cartType: cartType ?? "",
                    });
                    throw new Error("Received empty cart data from native");
                }

                const localCartData = toFlatLocalCart(localCart as ILocalCart | ILocalCartResponseV2);
                const cartStateMeta = cartState?.localCart?.cartMetaData;

                const updatedLocalCart: ILocalCart = createUpdatedLocalCart(localCartData, cartStateMeta);

                if (isStealStoreCart(cartType)) {
                    const { cacheExpired, latestCartResponse: nativeLatestCartResponse } = getLatestCartResponse(
                        localCart as ILocalCartResponseV2,
                    );

                    if (
                        !isEmpty(nativeLatestCartResponse) &&
                        !cacheExpired &&
                        !isEmpty(nativeLatestCartResponse?.data)
                    ) {
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.DINEOUT_WEBCART_RESPONSE_CACHING,
                            isCachedResponse: "true",
                            source: "dineout_non_hero_inline",
                            statusCode: nativeLatestCartResponse?.statusCode || "",
                            statusMessage: nativeLatestCartResponse?.statusMessage || "",
                            latestCartCallTimestamp: String(
                                (localCart as ILocalCartResponseV2)?.latestCartCallTimestamp,
                            ),
                        });
                        updateCartApiResponseHandler(Ok(nativeLatestCartResponse), updatedLocalCart, localCartData);
                        return;
                    }

                    recordNREvent({
                        eventType: NEWRELIC_TABLES.DINEOUT_WEBCART_RESPONSE_CACHING,
                        isCachedResponse: "false",
                        source: "dineout_non_hero_inline",
                        statusCode: nativeLatestCartResponse?.statusCode || "",
                        statusMessage: nativeLatestCartResponse?.statusMessage || "",
                        isDataNonEmpty: String(!isEmpty(nativeLatestCartResponse?.data)),
                        latestCartCallTimestamp: String((localCart as ILocalCartResponseV2)?.latestCartCallTimestamp),
                    });
                }

                updateDineoutCartWrapper(cartType, updatedLocalCart)
                    .then((response) => {
                        if (response.isErr()) {
                            // TODO: need to update this via  type: DineoutCartActionTypes.UPDATE_ERROR_PAYLOAD
                            setCartState({
                                type: DineoutCartActionTypes.UPDATE_LOCAL_CART,
                                payload: updatedLocalCart,
                            });
                            throw response;
                        }
                        updateCartApiResponseHandler(response, updatedLocalCart, localCartData);
                    })
                    .catch((err) => handleError(err, localCartData));
            })
            .catch(() => {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TABLE_RESERVATION_CART_LOCAL_CART_ERROR,
                    error: "bridge_call_failure_getCartData",
                    cartType: cartType ?? "",
                });
                try {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                        type: "FETCH_CART_DATA_FAILURE",
                    });
                } catch {
                    /* No-op*/
                }
                const errorObj: CartError = {
                    errorPayload: { statusMessage: OOPS_SOMETHING_WENT_WRONG },
                };
                setState({ loading: false, error: errorObj });
                apiCallRef.current = false;
            });
    }, [
        cartState?.localCart?.cartMetaData?.couponCode,
        cartState?.localCart?.cartMetaData?.restaurantTip,
        cartState?.localCart?.cartMetaData?.rewardsWidget,
        cartState?.localCart?.cartMetaData?.isPartialPaymentApplied,
        cartType,
        checkoutState.payload?.skipCartReload,
        fetchLocalCartFromNative,
        setCartState,
        updateCartApiResponseHandler,
    ]);

    // Named handler functions for event listeners to enable proper cleanup
    const handleCartApiFetched = useCallback(() => {
        if (typeof window !== "undefined" && window.cartApiData?.data) {
            try {
                const cartApiDataClone: IDineoutWindowCartApiData = cloneDeep(window.cartApiData);
                const cartData = cartApiDataClone?.data;

                if (!cartData) {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.DINEOUT_CART_INLINE_INVALID_LOCALCART,
                    });
                    throw new Error("Received empty cart data from native - Hero Inline");
                }

                const localCartData = toFlatLocalCart(cartApiDataClone.nativeLocalCart);

                updateCartApiResponseHandler(Ok(cartData), localCartData, localCartData);
                cartApiDataConsumedRef.current = true;
                // Clear the payload after consumption to prevent duplicate processing and re-triggering of listeners.
                // The inline script (dineout_inline_script.html) sets this data, and this hook consumes it via the CART_API_FETCHED listener.
                window.cartApiData.data = undefined;

                recordNREvent({
                    eventType: NEWRELIC_TABLES.DINEOUT_INLINE_WEB_CART_API_STATE,
                    state: CUSTOM_EVENTS.CART_API_FETCHED,
                });
            } catch (e) {
                cartApiDataConsumedRef.current = false;
                recordNREvent({
                    eventType: NEWRELIC_TABLES.DINEOUT_HERO_INLINE_EVENT_CAPTURE_ERROR,
                    body: JSON.stringify(e),
                });
                captureError(e);
            }
        }
    }, [updateCartApiResponseHandler]);

    const handleCartApiError = useCallback(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.DINEOUT_INLINE_WEB_CART_API_STATE,
            state: CUSTOM_EVENTS.CART_API_ERROR,
        });
        getCartData();
    }, [getCartData]);

    const handleFetchApiError = useCallback(() => {
        recordNREvent({
            eventType: NEWRELIC_TABLES.DINEOUT_INLINE_WEB_CART_API_STATE,
            state: CUSTOM_EVENTS.FETCH_API_ERROR,
        });
        getCartData();
    }, [getCartData]);

    // Effect to add and remove event listeners with proper cleanup
    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }

        document.addEventListener(CUSTOM_EVENTS.CART_API_FETCHED, handleCartApiFetched);
        document.addEventListener(CUSTOM_EVENTS.CART_API_ERROR, handleCartApiError);
        document.addEventListener(CUSTOM_EVENTS.FETCH_API_ERROR, handleFetchApiError);

        // Cleanup function to remove event listeners
        return () => {
            document.removeEventListener(CUSTOM_EVENTS.CART_API_FETCHED, handleCartApiFetched);
            document.removeEventListener(CUSTOM_EVENTS.CART_API_ERROR, handleCartApiError);
            document.removeEventListener(CUSTOM_EVENTS.FETCH_API_ERROR, handleFetchApiError);
        };
    }, [handleCartApiFetched, handleCartApiError, handleFetchApiError]);

    useEffect(() => {
        if (!state.loading || apiCallRef.current) {
            return;
        }

        apiCallRef.current = true;

        if (isScenesFullPaymentCart(cartType)) {
            getCartDataForScenesFullPayment();
        } else if (!state.cartState) {
            if (typeof window !== "undefined") {
                const xpHeroApiInline = window.isDineoutHeroApiInlineHtmlXp;
                const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
                const isAppPlatformFlow = isAppPlatform(platform);

                // Handling backward compatibility
                const isHeroInlineFlowNotEnabled =
                    isEmpty(xpHeroApiInline) ||
                    xpHeroApiInline === "false" ||
                    xpHeroApiInline === "NO_XP" ||
                    (isAppPlatformFlow && !window.direct_bridge);

                if (isHeroInlineFlowNotEnabled && !state.cartState) {
                    getCartData();
                    return;
                }
            }

            /*  Below steps if api fetched events were fired before listeners were attached
             *    1. Early Return on Loading State
             *    2. Handle Error State - fallback to get-cart
             *    3. Handle Success State - proceed to render saved response instead of calling update-cart
             */

            // The Hero Api Call will be considered terminated when any one of (success, error, fetchError) flag are set to TRUE
            // All the other cases will be ignored
            const isHeroInlineCallInProgress =
                typeof window !== "undefined" &&
                !isEmpty(window.cartApiData) &&
                !window.cartApiData?.success &&
                !window.cartApiData?.error &&
                !window.cartApiData?.fetchError;
            if (isHeroInlineCallInProgress) {
                return;
            }

            const cartApiData = window.cartApiData;

            if (
                !cartApiData ||
                isEmpty(cartApiData.data) ||
                !cartApiData.supported || // if fetch wasn't supported or fetch error . fetch api here
                cartApiData.fetchError ||
                cartApiData.error
            ) {
                // If we already consumed cartApiData in handleCartApiFetched (which clears .data), skip getCartData - we're already processing.
                if (!cartApiDataConsumedRef.current) {
                    getCartData();
                }
                return;
            }

            // If JS chunk is downloaded after the inline call is completed the we dispatch the success event again
            if (
                document !== undefined &&
                typeof window !== "undefined" &&
                window.isDineoutHeroApiInlineHtmlXp === "true"
            ) {
                const successEvent = new CustomEvent(CUSTOM_EVENTS.CART_API_FETCHED);
                document.dispatchEvent(successEvent);
            }
        }
    }, [getCartData, state.cartState, state.loading, getCartDataForScenesFullPayment, cartType]);

    return {
        loading: state.loading,
        error: state.error,
        selectedOfferId: state.selectedOfferId,
        selectedSlot: state.selectedSlot,
        uniqueIdForCart: uniqueIdForCart,
    };
};

export default useFetchDineoutCart;
